import { Autocomplete, CircularProgress, debounce, TextField } from '@mui/material'
import { DatePicker as MuiDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import axios from 'axios'
import { Dayjs } from 'dayjs'
import { useEffect, useRef, useState } from 'react'

export function DatePicker(props: {
  label?: string
  size?: 'small' | 'medium'
  value?: Dayjs | null
  disabled?: boolean
  className?: string
  fullWidth?: boolean
  error?: boolean
  helperText?: string
  onChange?: (date: Dayjs | null) => void
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void
}) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MuiDatePicker
        timezone="UTC"
        label={props.label}
        slotProps={{
          textField: {
            size: props.size,
            className: props.className,
            fullWidth: props.fullWidth,
            error: props.error,
            helperText: props.helperText,
            onBlur: props.onBlur,
            sx: {
              '& .MuiPickersInputBase-root': {
                background: 'white',
              },
            },
          },
        }}
        value={props.value}
        onChange={props.onChange}
        disabled={props.disabled}
      />
    </LocalizationProvider>
  )
}

export function CRMSearch(props: {
  object: 'Opportunity' | 'Account' | 'Contact'
  value: { id: string; label: string } | null
  label: string
  accountId?: string
  disabled?: boolean
  error?: boolean
  errorText?: string
  onChange: (e: React.SyntheticEvent<Element, Event>, value: { id: string; label: string } | null) => void
}) {
  const [searchInputValue, setSearchInputValue] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<{ id: string; label: string }[]>([])

  useEffect(() => {
    setSearchResults(props.value ? [{ id: props.value.id, label: props.value.label }] : [])
  }, [])

  const handleSearchOpps = (value: string, accountId?: string) => {
    if (value.length < 3) {
      setSearchResults([])
      return
    }

    setSearchLoading(true)
    axios
      .get(`/search${props.object}`, { params: { search: value, accountId: accountId } })
      .then(res => {
        setSearchResults(res.data.map((opp: { id: string; name: string }) => ({ id: opp.id, label: opp.name })))
      })
      .catch(err => {
        setSearchResults([])
        console.error(err)
      })
      .finally(() => {
        setSearchLoading(false)
      })
  }
  const debouncedHandleSearchOpps = useRef(debounce(handleSearchOpps, 500)).current

  return (
    <Autocomplete
      fullWidth
      disabled={props.disabled}
      options={searchResults}
      filterOptions={x => x}
      inputValue={searchInputValue}
      loading={searchLoading}
      renderInput={params => (
        <TextField
          {...params}
          label={props.label}
          error={props.error}
          helperText={props.error ? props.errorText : ''}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: searchLoading ? <CircularProgress size={20} /> : params.InputProps.endAdornment,
            },
          }}
        />
      )}
      size="small"
      value={props.value}
      onInputChange={(e, value, reason) => {
        setSearchInputValue(value)
        if (reason !== 'selectOption' && reason !== 'reset') {
          debouncedHandleSearchOpps(value, props.object === 'Contact' ? props.accountId : undefined)
        }
      }}
      onChange={props.onChange}
    />
  )
}

export function CurrencyInput(props: {
  value?: number | null
  required?: boolean
  isInvalid?: boolean
  label?: string
  size?: 'small' | 'medium'
  fullWidth?: boolean
  disabled?: boolean
  ref?: React.RefObject<HTMLInputElement>
  onChange: (value: number | null) => void
  onBlur?: (value: number | null) => void
}) {
  const [cursor, setCursor] = useState<number | null>(0)
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    ref.current?.setSelectionRange(cursor, cursor)
  }, [ref, cursor, props.value])

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? null : e.target.value.replace('$', '').replaceAll(',', '')
    setCursor(e.target.selectionStart)

    if (!e.target.value || e.target.value === '$') {
      props.onChange(null)
      setCursor(1)
    } else if (!isNaN(Number(value))) {
      props.onChange(Number(value))
      if (
        e.target.value.length + 1 ===
        Number(value).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).length
      ) {
        setCursor(prev => (prev ?? 0) + 1)
      }
    }
  }

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? null : e.target.value.replace('$', '').replaceAll(',', '')
    setCursor(e.target.selectionStart)

    if (!e.target.value || e.target.value === '$') {
      props.onBlur?.(null)
      setCursor(1)
    } else if (!isNaN(Number(value))) {
      props.onBlur?.(Number(value))
    }
  }

  return (
    <TextField
      fullWidth={props.fullWidth}
      label={props.label}
      size={props.size}
      value={
        props.value === null || props.value === undefined
          ? '$'
          : props.value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
      }
      onChange={onChange}
      onBlur={onBlur}
      inputRef={ref}
      disabled={props.disabled}
    />
  )
}

export const formatCurrency = (value: number | undefined): string =>
  value ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '$0.00'
